interface ProjectSummary {
    id: string;
    lastOpenedAt?: string | null;
}

interface ResolveInitialProjectOptions {
    createProjectName: () => string;
    csrfToken: string;
    fetchImpl?: typeof fetch;
    onProjectCreated?: () => void;
    storage?: Storage;
    userId: string;
}

export const lastProjectStorageKey = (userId: string): string =>
    `scratch-cg-last-project:${userId}`;

export const rememberLastProject = (
    userId: string,
    projectId: string,
    storage: Storage = window.localStorage,
): void => {
    storage.setItem(lastProjectStorageKey(userId), projectId);
};

export const forgetLastProject = (
    userId: string,
    storage: Storage = window.localStorage,
): void => {
    storage.removeItem(lastProjectStorageKey(userId));
};

export const recordProjectOpen = async (
    projectId: string,
    csrfToken: string,
    fetchImpl: typeof fetch = fetch,
): Promise<void> => {
    const response = await fetchImpl(`/api/projects/${projectId}/open`, {
        method: "POST",
        headers: {"X-CSRF-Token": csrfToken},
    });
    if (!response.ok) throw new Error("PROJECT_OPEN_RECORD_FAILED");
};

export const resolveInitialProject = async ({
    createProjectName,
    csrfToken,
    fetchImpl = fetch,
    onProjectCreated,
    storage = window.localStorage,
    userId,
}: ResolveInitialProjectOptions): Promise<string> => {
    const response = await fetchImpl("/api/projects");
    if (!response.ok) throw new Error("PROJECT_LIST_FAILED");
    const { projects } = (await response.json()) as {
        projects: ProjectSummary[];
    };
    const remembered = storage.getItem(lastProjectStorageKey(userId));
    const recentlyOpened = projects.reduce<ProjectSummary | undefined>((best, project) => {
        if (!project.lastOpenedAt || !Number.isFinite(Date.parse(project.lastOpenedAt))) return best;
        if (!best?.lastOpenedAt || Date.parse(project.lastOpenedAt) > Date.parse(best.lastOpenedAt))
            return project;
        return best;
    }, undefined);
    const selected = recentlyOpened ?? projects.find((project) => project.id === remembered) ?? projects.at(0);
    if (selected) {
        rememberLastProject(userId, selected.id, storage);
        return selected.id;
    }

    const created = await fetchImpl("/api/projects", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ name: createProjectName() }),
    });
    if (!created.ok) throw new Error("PROJECT_CREATE_FAILED");
    const body = (await created.json()) as { project: ProjectSummary };
    onProjectCreated?.();
    rememberLastProject(userId, body.project.id, storage);
    return body.project.id;
};
